import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { listUsers } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { startImpersonation } from "../../auth/impersonation.js";
import { ListRow } from "../molecules/ListRow.js";
import { Modal } from "../shell/Modal.js";
import { SearchInput } from "../ui/SearchInput.js";
import { Badge, Button, EmptyState, ErrorState, useT } from "@mmm/ui";
/**
 * Superadmin-only user picker. Rendered only where the caller has already
 * confirmed `session.actor.role === "superadmin"` — but `listUsers` is
 * independently authorized server-side, so a mis-render leaks nothing.
 */
interface ImpersonationPickerProps {
  /** Dismiss the picker, returning to wherever it was opened from. */
  onClose: () => void;
  /**
   * Called instead of `onClose` once a switch actually happens, so the caller
   * can tear down its own chrome too — landing in someone else's account
   * behind a still-open Settings dialog reads as though nothing happened.
   */
  onImpersonated: () => void;
}

export function ImpersonationPicker({ onClose, onImpersonated }: ImpersonationPickerProps) {
  const [search, setSearch] = useState("");
  const usersQuery = useQuery(listUsers, { search: search.trim() || undefined });
  const navigate = useNavigate();
  const t = useT();

  function impersonate(userId: string) {
    startImpersonation(userId);
    onImpersonated();
    // "/dashboard", deliberately — NOT "/" (the public marketing landing page,
    // which drops you out of the app entirely) and NOT "/home" (which is
    // LastViewedPageRedirect: it would restore the PREVIOUS identity's subject
    // or chapter, whose ids the impersonated user doesn't own). The dashboard
    // is the one app entry that belongs to whoever you currently are.
    void navigate("/dashboard");
  }

  return (
    <Modal onClose={onClose} width={520}>
      <h2>{t("admin.impersonateTitle")}</h2>
      <p className="mmm-modal-lead">{t("admin.impersonateLead")}</p>

      {/* No placeholder override — SearchInput already defaults to the shared
          common.search label, and the i18n convention is one key per label. */}
      <SearchInput value={search} onChange={setSearch} />

      <div className="mmm-admin-user-list">
        {usersQuery.error ? (
          <ErrorState variant="list" title={t("admin.usersLoadFailed")} onRetry={usersQuery.retry} />
        ) : usersQuery.data?.length === 0 ? (
          <EmptyState variant="list" title={t("admin.noUsersFound")} message={t("admin.noUsersFoundHint")} />
        ) : (
          usersQuery.data?.map((user) => (
            <ListRow
              key={user.id}
              className="mmm-admin-user-row"
              title={user.displayName}
              meta={user.email}
              trailing={
                <span className="mmm-admin-user-actions">
                  {user.role === "superadmin" && <Badge tone="neutral">{t("admin.roleSuperadmin")}</Badge>}
                  <Button variant="secondary" onClick={() => impersonate(user.id)}>
                    {t("admin.impersonate")}
                  </Button>
                </span>
              }
            />
          ))
        )}
      </div>

      <div className="mmm-modal-actions">
        <Button variant="primary" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </Modal>
  );
}
